import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtCurrency, fmtDateTime } from "@/lib/format";
import { SelectField, useAssignees } from "@/lib/crm";
import { ACTION_TYPES } from "@/lib/sales";
import { PageHeader, Panel, StatusBadge } from "@/components/common/ui-kit";

const BUCKETS = [["overdue", "Scaduti", "red"], ["today", "Oggi", "amber"], ["upcoming", "In arrivo", "blue"], ["none", "Senza azione", "neutral"], ["completed", "Completati (30 giorni)", "green"]];

const Row = ({ o, onDone }) => (
  <li className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:gap-4" data-testid={`followup-row-${o.id}`}>
    {o.next_action?.activity_id && !o.completed_at && <button onClick={() => api.post(`/activities/${o.next_action.activity_id}/complete`, {}).then(() => { toast.success("Completata"); onDone(); }).catch((e) => toast.error(errorMessage(e)))}
      className="self-start rounded-full border-2 border-slate-300 px-2 text-xs font-semibold text-slate-500 hover:border-emerald-500 hover:text-emerald-600" data-testid={`followup-complete-${o.id}`}>Completa</button>}
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-ink">{o.next_action ? o.next_action.title : "Pianificare la prossima azione"}
        {o.next_action?.type && <span className="ml-2 text-xs font-normal text-slate-500">{ACTION_TYPES.find((t) => t.key === o.next_action.type)?.label}</span>}</p>
      <p className="text-xs text-slate-500"><Link to={`/opportunita/${o.id}`} className="font-medium text-brand-700 hover:underline" data-testid={`followup-opp-link-${o.id}`}>{o.title}</Link>
        {" · "}<Link to={`/clienti/${o.company_id}`} className="hover:underline">{o.company_name}</Link> · {fmtCurrency(o.value)}</p>
    </div>
    <div className="flex items-center gap-3 text-xs text-slate-600">
      {o.next_action && <span className={o.followup_overdue ? "font-semibold text-red-600" : ""}>{fmtDateTime(o.next_action_at)}</span>}
      <span>{o.next_action?.owner_name || o.owner_name}</span>
      <StatusBadge tone={o.stage?.tone}>{o.stage?.name}</StatusBadge>
    </div>
  </li>
);

export default function FollowupPage() {
  const users = useAssignees();
  const [owner, setOwner] = useState("");
  const [data, setData] = useState({});
  const load = useCallback(() => {
    const params = owner ? { owner_id: owner } : {};
    Promise.all(BUCKETS.map(([k]) => api.get(`/sales/followups/${k}`, { params }).then((r) => [k, r.data.items])))
      .then((rows) => setData(Object.fromEntries(rows))).catch((e) => toast.error(errorMessage(e)));
  }, [owner]);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("focus", load);
    return () => { clearInterval(t); window.removeEventListener("focus", load); };
  }, [load]);

  return (
    <div data-testid="followup-page">
      <PageHeader overline="Sales" title="Follow-up" description="Le prossime azioni sulle opportunità aperte: cosa è in ritardo, cosa fare oggi, cosa arriva."
        actions={<SelectField value={owner} onChange={setOwner} options={users} allowNone="Tutti i responsabili" testId="followup-filter-owner" className="w-56" />} />
      <div className="space-y-6">
        {BUCKETS.map(([k, label, tone]) => (
          <Panel key={k} className="p-5" data-testid={`followup-bucket-${k}`}>
            <div className="flex items-center gap-2"><StatusBadge tone={tone}>{label}</StatusBadge><span className="text-sm font-semibold text-slate-600" data-testid={`followup-count-${k}`}>{data[k]?.length ?? "…"}</span></div>
            {data[k]?.length === 0 ? <p className="mt-3 text-sm text-slate-500">Niente da segnalare.</p> : <ul className="mt-1 divide-y divide-slate-100">{(data[k] || []).map((o) => <Row key={`${o.id}-${o.next_action?.activity_id}`} o={o} onDone={load} />)}</ul>}
          </Panel>
        ))}
      </div>
    </div>
  );
}
